import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GoogleIcon from "@mui/icons-material/Google";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import useAuthentication from "@hooks/useAuthentication";
import usePopup from "../../hooks/usePopup";
import CustomTableBody from "../../utilis/component/CustomTableBody";
import {
  createPatientQuestionnaire,
  disconnectGoogleOAuth,
  getGoogleOAuthConnectUrl,
  getGoogleOAuthStatus,
  getPatientQuestionnaires,
} from "./services";
import {
  GOOGLE_OAUTH_STATUS_QUERY_KEY,
  PATIENT_QUESTIONNAIRES_QUERY_KEY,
  PatientQuestionnaireSearchParams,
} from "./types";
import dayjs from "dayjs";

const TABLE_COLUMNS = 8;
const GOOGLE_OAUTH_STATE_KEY = "google_oauth_state";

function CreateFormPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showSnackbar, CustomModal } = usePopup();
  const { user } = useAuthentication();
  const doctorId = Number(user?.clinicianID || user?.login_id || 0);
  const createdBy = Number(user?.login_id || user?.clinicianID || 0);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const listParams: PatientQuestionnaireSearchParams = useMemo(
    () => ({
      doctorId,
      pageNumber,
      pageSize,
    }),
    [doctorId, pageNumber, pageSize],
  );

  const listQueryKey = [
    PATIENT_QUESTIONNAIRES_QUERY_KEY,
    doctorId,
    pageNumber,
    pageSize,
  ] as const;

  const { data, isLoading, isFetching } = useQuery(
    listQueryKey,
    () => getPatientQuestionnaires(listParams),
    {
      enabled: doctorId > 0,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const items = data?.response?.items ?? [];
  const totalCount = data?.response?.totalCount ?? 0;

  const {
    data: oauthStatusData,
    isLoading: isOAuthStatusLoading,
  } = useQuery(
    [GOOGLE_OAUTH_STATUS_QUERY_KEY],
    getGoogleOAuthStatus,
    {
      refetchOnWindowFocus: false,
    },
  );

  const isGoogleConnected =
    oauthStatusData?.responseIndicator === "success" &&
    oauthStatusData?.response?.connected === true;
  const connectedGmailAccount = oauthStatusData?.response?.gmailAccount;

  const redirectToGoogleAuth = useCallback(
    (result: Awaited<ReturnType<typeof getGoogleOAuthConnectUrl>>) => {
      if (result.responseIndicator === "success" && result.response?.authUrl) {
        try {
          const authUrl = new URL(result.response.authUrl);
          const state = authUrl.searchParams.get("state");
          if (state) {
            sessionStorage.setItem(GOOGLE_OAUTH_STATE_KEY, state);
          }
        } catch {
          // ignore URL parse errors; redirect still proceeds
        }

        window.location.href = result.response.authUrl;
        return;
      }

      showSnackbar({
        message:
          result.responseMessage || "Failed to start Google authentication.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    },
    [showSnackbar],
  );

  const showGoogleAuthError = useCallback(
    (error: any, fallbackMessage: string) => {
      showSnackbar({
        message:
          error?.response?.data?.responseMessage ||
          error?.response?.data?.errorMessage ||
          error?.message ||
          fallbackMessage,
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
    [showSnackbar],
  );

  const loadFormList = useCallback(
    async (params: PatientQuestionnaireSearchParams) => {
      await queryClient.fetchQuery(
        [
          PATIENT_QUESTIONNAIRES_QUERY_KEY,
          params.doctorId,
          params.pageNumber,
          params.pageSize,
        ],
        () => getPatientQuestionnaires(params),
      );
    },
    [queryClient],
  );

  const refreshFormList = useCallback(
    async (overrides?: { pageNumber?: number; pageSize?: number }) => {
      const nextPageNumber = overrides?.pageNumber ?? pageNumber;
      const nextPageSize = overrides?.pageSize ?? pageSize;

      if (overrides?.pageNumber !== undefined) {
        setPageNumber(nextPageNumber);
      }
      if (overrides?.pageSize !== undefined) {
        setPageSize(nextPageSize);
      }

      const nextParams: PatientQuestionnaireSearchParams = {
        doctorId,
        pageNumber: nextPageNumber,
        pageSize: nextPageSize,
      };

      await queryClient.invalidateQueries([PATIENT_QUESTIONNAIRES_QUERY_KEY]);
      await loadFormList(nextParams);
    },
    [doctorId, loadFormList, pageNumber, pageSize, queryClient],
  );

  const { mutate: connectGoogle, isLoading: isConnectingGoogle } = useMutation(
    getGoogleOAuthConnectUrl,
    {
      onSuccess: redirectToGoogleAuth,
      onError: (error: any) => {
        showGoogleAuthError(error, "Failed to connect Google account.");
      },
    },
  );

  const { mutate: refreshGoogle, isLoading: isRefreshingGoogle } = useMutation(
    async () => {
      const disconnectResult = await disconnectGoogleOAuth();
      if (disconnectResult.responseIndicator !== "success") {
        throw new Error(
          disconnectResult.responseMessage || "Failed to disconnect Google account.",
        );
      }
      return getGoogleOAuthConnectUrl();
    },
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries([GOOGLE_OAUTH_STATUS_QUERY_KEY]);
        redirectToGoogleAuth(result);
      },
      onError: (error: any) => {
        showGoogleAuthError(error, "Failed to refresh Google connection.");
      },
    },
  );

  const handleConnectGoogle = () => {
    connectGoogle();
  };

  const handleRefreshGoogle = () => {
    refreshGoogle();
  };

  const { mutate: createForm, isLoading: isCreating } = useMutation(
    createPatientQuestionnaire,
    {
      onSuccess: async (result) => {
        if (result.responseIndicator === "success") {
          showSnackbar({
            message:
              result.responseMessage ||
              "Patient questionnaire form created successfully",
            open: true,
            variant: "success",
            duration: 3000,
          });
          setFormTitle("");
          setCreateModalOpen(false);
          await refreshFormList({ pageNumber: 0 });
          return;
        }

        showSnackbar({
          message: result.responseMessage || "Failed to create form.",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
      onError: (error: any) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            error?.response?.data?.errorMessage ||
            "Failed to create form.",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    },
  );

  const handleCreateForm = () => {
    const title = formTitle.trim();

    if (!title) {
      showSnackbar({
        message: "Please enter a form name.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    if (!doctorId || !createdBy) {
      showSnackbar({
        message: "Doctor or user information is missing.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    createForm({ title, doctorId, createdBy });
  };

  const handleOpenCreateModal = () => {
    setFormTitle("");
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (isCreating) return;
    setCreateModalOpen(false);
    setFormTitle("");
  };

  const handleChangePage = (_: unknown, nextPageNumber: number) => {
    setPageNumber(nextPageNumber);
    loadFormList({ doctorId, pageNumber: nextPageNumber, pageSize });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextPageSize = parseInt(event.target.value, 10);
    setPageNumber(0);
    setPageSize(nextPageSize);
    loadFormList({ doctorId, pageNumber: 0, pageSize: nextPageSize });
  };

  const handleExternalFormOpen = () => {
    window.addEventListener(
      "focus",
      () => {
        refreshFormList();
      },
      { once: true },
    );
  };

  const tableLoading = isLoading || isFetching;

  return (
    <Box className="p-4">
      <CustomModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        title="Create Form"
        handleClose={handleCloseCreateModal}
      >
        <Box className="w-full min-w-[320px] max-w-md p-4 flex flex-col gap-4">
          <TextField
            label="Form Name"
            fullWidth
            autoFocus
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="e.g. Patient Intake Form"
            disabled={isCreating}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateForm();
            }}
          />
          <Box className="flex justify-end gap-2">
            <Button
              variant="outlined"
              onClick={handleCloseCreateModal}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateForm}
              disabled={isCreating || !doctorId || !createdBy}
              endIcon={
                isCreating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </Box>
        </Box>
      </CustomModal>

      <Paper className="p-4">
        <Box className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <Typography variant="h5" fontWeight={700} color="primary">
            Create Form
          </Typography>
          <Box className="flex flex-wrap items-center gap-2">
            {isOAuthStatusLoading ? (
              <CircularProgress size={22} />
            ) : isGoogleConnected ? (
              <>
                {connectedGmailAccount ? (
                  <Typography variant="body2" color="text.secondary">
                    Connected: {connectedGmailAccount}
                  </Typography>
                ) : null}
                <Button
                  variant="outlined"
                  startIcon={
                    isRefreshingGoogle ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={handleRefreshGoogle}
                  disabled={isRefreshingGoogle}
                >
                  {isRefreshingGoogle ? "Refreshing..." : "Refresh"}
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={
                  isConnectingGoogle ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <GoogleIcon />
                  )
                }
                onClick={handleConnectGoogle}
                disabled={isConnectingGoogle}
              >
                {isConnectingGoogle ? "Connecting..." : "Connect with Google"}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
              disabled={!doctorId || !createdBy}
            >
              Create Form
            </Button>
          </Box>
        </Box>

        <TableContainer
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "Title",
                  "Description",
                  "Status",
                  "Google Form ID",
                  "Responder Link",
                  "Edit Link",
                  "Created At",
                  "Updated At",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <CustomTableBody
              isData={items.length > 0}
              dataLoading={tableLoading}
              totalSpan={{ row: 5, col: TABLE_COLUMNS }}
              alert="No forms found"
            >
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:not(:last-child) td": {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.description || "-"}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }} className="truncate">
                    {item.googleFormId}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={item.responderUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleExternalFormOpen}
                    >
                      View Form
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={item.editUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleExternalFormOpen}
                    >
                      Edit Form
                    </Link>
                  </TableCell>
                  <TableCell>
                    {dayjs(item.createdAt).format("DD-MM-YYYY HH:mm")}
                  </TableCell>
                  <TableCell>
                    {dayjs(item.updatedAt).format("DD-MM-YYYY HH:mm")}
                  </TableCell>
                </TableRow>
              ))}
            </CustomTableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={pageNumber}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
}

export default CreateFormPage;
